// Object Selection Tool — drag a rect or lasso region to auto-select the
// dominant object inside it using the heuristic SubjectDetector.
//
// Photoshop W flyout: Object Selection / Quick Selection / Magic Wand.
// Behaviour:
//   1. User drags a rectangular region on the canvas.
//   2. Tool crops the active layer's pixels to that region and runs
//      detectSubject() on the crop.
//   3. The resulting selection mask (in crop coordinates) is expanded back
//      to document coordinates and committed via SetSelectionCommand.
//
// Modifier convention (matches MarqueeTool / LassoTool / MagicWandTool):
//   default      → replace
//   shift        → add (union)
//   alt          → subtract
//   shift + alt  → intersect
//
// DOM-free requirement: the pixel cropping is pure TypedArray work.
// Layer: 3 (Document Model). MUST remain DOM-free / browser-free.

import { SetSelectionCommand } from "../commands/SetSelectionCommand";
import { Selection } from "../selection/Selection";
import { detectSubject } from "../selection/SubjectDetector";
import { screenToDoc } from "../render/Viewport";
import type { PointerSample, Tool, ToolContext } from "./Tool";

export type CombineMode = "replace" | "add" | "subtract" | "intersect";

/**
 * Half-size (in document px) of the box seeded around a plain click's
 * coordinates when the user clicks without dragging. Photoshop's Object
 * Selection responds to a click by segmenting "the object under the
 * cursor"; since our heuristic detector needs a bounded region to analyze
 * (see detectSubject), a click synthesizes a small region centered on the
 * click point and runs the exact same crop + detectSubject + expand
 * pipeline that a dragged rectangle already uses.
 */
const CLICK_SEED_HALF_SIZE = 12;

export class ObjectSelectionTool implements Tool {
  readonly id = "object-selection";
  readonly shortcut = "W";
  /**
   * Explicit combine-mode override set by the options bar's New/Add/
   * Subtract/Intersect button strip (ToolUX's wireCombineModeButtons,
   * audit i60/i162). When set to "add"/"subtract"/"intersect" it wins over
   * the shift/alt-derived default computed at pointer-up; "new" (or unset)
   * falls back to the existing shift/alt modifier behavior.
   */
  combineMode?: "new" | "add" | "subtract" | "intersect";

  private dragStart: { x: number; y: number } | null = null;
  private dragEnd: { x: number; y: number } | null = null;

  onPointerDown(ctx: ToolContext, s: PointerSample): void {
    const p = screenToDoc(ctx.viewport, s.x, s.y);
    this.dragStart = { x: Math.round(p.x), y: Math.round(p.y) };
    this.dragEnd = { ...this.dragStart };
    ctx.requestRender();
  }

  onPointerMove(ctx: ToolContext, s: PointerSample): void {
    if (!this.dragStart) return;
    const p = screenToDoc(ctx.viewport, s.x, s.y);
    this.dragEnd = { x: Math.round(p.x), y: Math.round(p.y) };
    ctx.requestRender();
  }

  onPointerUp(ctx: ToolContext, s: PointerSample): void {
    if (!this.dragStart || !ctx.doc) {
      this.dragStart = null;
      this.dragEnd = null;
      return;
    }
    const p = screenToDoc(ctx.viewport, s.x, s.y);
    this.dragEnd = { x: Math.round(p.x), y: Math.round(p.y) };
    const combine = pickCombine(s.shiftKey, s.altKey, this.combineMode);

    const docW = ctx.doc.width;
    const docH = ctx.doc.height;

    // Compute the dragged region in document coordinates, clamped.
    const dragX0 = Math.max(0, Math.min(this.dragStart.x, this.dragEnd.x));
    const dragY0 = Math.max(0, Math.min(this.dragStart.y, this.dragEnd.y));
    const dragX1 = Math.min(docW, Math.max(this.dragStart.x, this.dragEnd.x));
    const dragY1 = Math.min(docH, Math.max(this.dragStart.y, this.dragEnd.y));
    const dragW = dragX1 - dragX0;
    const dragH = dragY1 - dragY0;

    this.dragStart = null;
    this.dragEnd = null;

    // A plain click (pointer didn't move between down and up) produces a
    // zero-size drag rect. Rather than no-op, seed detection from a small
    // fixed-size box centered on the click point — see CLICK_SEED_HALF_SIZE.
    const isClick = dragW === 0 && dragH === 0;

    let rx0: number, ry0: number, rx1: number, ry1: number;
    if (isClick) {
      rx0 = Math.max(0, dragX0 - CLICK_SEED_HALF_SIZE);
      ry0 = Math.max(0, dragY0 - CLICK_SEED_HALF_SIZE);
      rx1 = Math.min(docW, dragX0 + CLICK_SEED_HALF_SIZE);
      ry1 = Math.min(docH, dragY0 + CLICK_SEED_HALF_SIZE);
    } else {
      rx0 = dragX0;
      ry0 = dragY0;
      rx1 = dragX1;
      ry1 = dragY1;
    }
    const rw = rx1 - rx0;
    const rh = ry1 - ry0;

    // A real (non-click) drag that's too thin to be a meaningful region is
    // still a no-op. Click-seeded boxes only bail if they collapse to
    // nothing (e.g. clicking a 0-size document).
    if (!isClick && (rw < 4 || rh < 4)) {
      ctx.requestRender();
      return;
    }
    if (rw < 1 || rh < 1) {
      ctx.requestRender();
      return;
    }

    // Find the active raster layer for pixel sampling.
    const activeLayer = ctx.doc.activeLayerId
      ? findRasterLayer(ctx.doc.layers as unknown as readonly RasterLike[], ctx.doc.activeLayerId)
      : null;

    let regionPixels: Uint8ClampedArray | Uint8Array;

    if (activeLayer) {
      // Crop the layer pixels to the region. Layer pixels may be offset.
      const lx = activeLayer.x;
      const ly = activeLayer.y;
      const lw = activeLayer.pixels.width;
      const lh = activeLayer.pixels.height;

      regionPixels = cropRgba(
        activeLayer.pixels.data,
        lw, lh, lx, ly,
        rx0, ry0, rw, rh,
      );
    } else {
      // No active layer — produce a non-empty opaque gray buffer so the
      // detector still runs (it will pick the centered region as subject).
      regionPixels = makeSolidRgba(rw, rh, 128, 128, 128, 255);
    }

    // Run subject detection on the cropped region.
    const cropSel = detectSubject(regionPixels, rw, rh, {
      edgeSensitivity: 0.5,
      centerBias: 0.4,
      borderBias: 0.6,
      closeRadius: 2,
    });

    // Expand crop-space selection to full document space.
    const docMask = new Uint8Array(docW * docH);
    for (let cy = 0; cy < rh; cy++) {
      const dy = ry0 + cy;
      if (dy < 0 || dy >= docH) continue;
      for (let cx = 0; cx < rw; cx++) {
        const dx = rx0 + cx;
        if (dx < 0 || dx >= docW) continue;
        docMask[dy * docW + dx] = cropSel.mask[cy * rw + cx] ?? 0;
      }
    }

    const sel = new Selection(docW, docH, docMask);
    const next = combineSelection(combine, ctx.doc.selection, sel);
    if (next !== null) {
      ctx.emit(new SetSelectionCommand(next));
    }
    ctx.requestRender();
  }

  /** Expose current drag rect for renderer overlay. */
  getDragRect(): { x: number; y: number; w: number; h: number } | null {
    if (!this.dragStart || !this.dragEnd) return null;
    const x = Math.min(this.dragStart.x, this.dragEnd.x);
    const y = Math.min(this.dragStart.y, this.dragEnd.y);
    const w = Math.abs(this.dragEnd.x - this.dragStart.x);
    const h = Math.abs(this.dragEnd.y - this.dragStart.y);
    return { x, y, w, h };
  }
}

// ---------------------------------------------------------------------------
// Pure helpers — no DOM, no imports beyond what's already imported.
// ---------------------------------------------------------------------------

interface RasterLike {
  id: string;
  kind: string;
  pixels: { data: Uint8ClampedArray; width: number; height: number };
  x: number;
  y: number;
  children?: readonly RasterLike[];
}

function findRasterLayer(layers: readonly RasterLike[], id: string): RasterLike | null {
  for (const l of layers) {
    if (l.kind === "raster" && l.id === id) return l;
    if (l.kind === "group" && l.children) {
      const found = findRasterLayer(l.children, id);
      if (found) return found;
    }
  }
  return null;
}

/**
 * Crop an RGBA pixel buffer to a rectangular region in document space.
 * The layer's top-left in document space is (lx, ly). The region is
 * (rx0, ry0, rw, rh) in document space. Returns an rw*rh*4 RGBA array.
 */
function cropRgba(
  data: Uint8ClampedArray,
  lw: number,
  lh: number,
  lx: number,
  ly: number,
  rx0: number,
  ry0: number,
  rw: number,
  rh: number,
): Uint8Array {
  const out = new Uint8Array(rw * rh * 4);
  for (let cy = 0; cy < rh; cy++) {
    // layer-local y of this row
    const srcY = ry0 + cy - ly;
    if (srcY < 0 || srcY >= lh) continue;
    for (let cx = 0; cx < rw; cx++) {
      const srcX = rx0 + cx - lx;
      if (srcX < 0 || srcX >= lw) continue;
      const si = (srcY * lw + srcX) * 4;
      const di = (cy * rw + cx) * 4;
      out[di] = data[si] ?? 0;
      out[di + 1] = data[si + 1] ?? 0;
      out[di + 2] = data[si + 2] ?? 0;
      out[di + 3] = data[si + 3] ?? 0;
    }
  }
  return out;
}

function makeSolidRgba(w: number, h: number, r: number, g: number, b: number, a: number): Uint8Array {
  const out = new Uint8Array(w * h * 4);
  for (let i = 0; i < w * h; i++) {
    out[i * 4] = r;
    out[i * 4 + 1] = g;
    out[i * 4 + 2] = b;
    out[i * 4 + 3] = a;
  }
  return out;
}

function pickCombine(shift: boolean, alt: boolean, override?: string): CombineMode {
  // An explicit options-bar combine-mode selection (audit i60/i162) wins over
  // the shift/alt-derived default. "new" (or no override) falls through to
  // the existing modifier-key behavior below.
  if (override === "add" || override === "subtract" || override === "intersect") {
    return override;
  }
  if (shift && alt) return "intersect";
  if (shift) return "add";
  if (alt) return "subtract";
  return "replace";
}

/**
 * Combine the freshly detected `local` selection with the document's
 * pre-existing selection according to `mode`. Mirrors MagicWandTool /
 * LassoTool's combine semantics:
 *   replace   → local (or null if local is empty — preserves legacy
 *               behavior of leaving the existing selection untouched when
 *               the detector finds nothing).
 *   add       → union(original, local); falls back to local/original alone
 *               when the other side is missing/empty.
 *   subtract  → original with local carved out; no-op without an original.
 *   intersect → overlap of original and local; empty/null without both.
 */
function combineSelection(
  mode: CombineMode,
  original: Selection | null,
  local: Selection | null,
): Selection | null {
  switch (mode) {
    case "replace":
      return local && !local.isEmpty() ? local : null;
    case "add":
      if (!original) return local && !local.isEmpty() ? local : null;
      if (!local || local.isEmpty()) return original;
      return original.union(local);
    case "subtract":
      if (!original) return null;
      if (!local || local.isEmpty()) return original;
      return original.subtract(local);
    case "intersect":
      if (!original) return null;
      if (!local || local.isEmpty()) return null;
      return original.intersect(local);
  }
}
